// Learner Profile — aggregates all engine state into a single profile object
// Multi-cert: reads cert-scoped data via certPath(), calibration from globalPath()

import { certPath, globalPath, read } from './firebase-store.js';
import { getRetrievability } from './scheduler.js';
import { getSkillMastery } from './mastery.js';
import { getCalibrationScore } from './calibration.js';
import { DEFAULT_DOMAIN_WEIGHTS, MASTERED_STABILITY_DAYS } from './constants.js';
import { isGraphReady, getConceptMastery, computeCoverage, getWeakConcepts } from './knowledge-graph.js';

/**
 * Pure function: compute exam readiness score from profile data.
 * Accepts domainWeights as parameter instead of importing globals.
 *
 * @param {object} params
 * @param {object} params.domainMastery - { d1: 0.5, d2: ... }
 * @param {object|null} params.coverageData - { coverage, totalSkills, domains }
 * @param {object} params.cards - { [qid]: card } FSRS card states
 * @param {number|null} params.calibrationScore - ECE score 0-1
 * @param {object} params.domainWeights - { 1: w1, 2: w2, ... }
 * @returns {{ predictedPassRate: number, readinessBreakdown: object }}
 */
export function computeReadinessScore({ domainMastery, coverageData, cards, calibrationScore, domainWeights }) {
  const weights = domainWeights || DEFAULT_DOMAIN_WEIGHTS;
  const domainKeys = Object.keys(weights).map(Number);

  // 1. Base: weighted domain mastery from theta (logistic IRT model)
  let baseDomainScore = 0;
  for (const d of domainKeys) {
    baseDomainScore += (domainMastery['d' + d] || 0.5) * weights[d];
  }

  // 2. Coverage factor
  let coverageFactor = 1.0;
  if (coverageData && coverageData.totalSkills > 0) {
    coverageFactor = 0.3 + 0.7 * coverageData.coverage;
  }

  // 3. Stability factor
  let stabilityFactor = 1.0;
  const cardEntries = cards ? Object.entries(cards) : [];
  if (cardEntries.length > 0) {
    let totalRetrievability = 0;
    let cardCount = 0;
    for (const [, card] of cardEntries) {
      const r = getRetrievability(card);
      if (r !== null) {
        totalRetrievability += r;
        cardCount++;
      }
    }
    if (cardCount > 0) {
      const avgRetrievability = totalRetrievability / cardCount;
      stabilityFactor = Math.min(1.0, avgRetrievability / 0.7);
    }
  }

  // 4. Calibration adjustment
  let calibrationAdjustment = 1.0;
  if (calibrationScore !== null && calibrationScore < 0.5) {
    calibrationAdjustment = 0.85 + 0.15 * (calibrationScore * 2);
  }

  // Final readiness score
  let predictedPassRate = baseDomainScore * coverageFactor * stabilityFactor * calibrationAdjustment;
  predictedPassRate = Math.max(0, Math.min(1, predictedPassRate));

  const readinessBreakdown = {
    baseDomainScore: Math.round(baseDomainScore * 100) / 100,
    coverageFactor: Math.round(coverageFactor * 100) / 100,
    stabilityFactor: Math.round(stabilityFactor * 100) / 100,
    calibrationAdjustment: Math.round(calibrationAdjustment * 100) / 100
  };

  return { predictedPassRate, readinessBreakdown };
}

/**
 * Aggregate all engine state into a profile for the UI (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} certSpec - { domainWeights: { 1: w1, ... }, displayName: '...' }
 * @returns {object} full learner profile
 */
export async function getLearnerProfile(userId, certId, certSpec) {
  // Read cert-scoped data
  const cards = await read(certPath(userId, certId, 'cards')) || {};
  const theta = await read(certPath(userId, certId, 'profile/theta')) || {};
  const sessions = await read(certPath(userId, certId, 'sessions')) || {};

  // Calibration is user-global
  const confidenceMatrix = await read(globalPath(userId, 'confidenceMatrix')) || null;

  // Resolve cert-specific domain weights from certSpec
  const certWeights = (certSpec && certSpec.domainWeights) || DEFAULT_DOMAIN_WEIGHTS;
  const domainKeys = Object.keys(certWeights).map(Number);

  // Compute per-domain stats
  const domainMastery = {};
  let totalSeen = 0;
  let totalMastered = 0;
  let totalInLearning = 0;
  let totalDueToday = 0;
  const now = new Date();

  for (const [qid, card] of Object.entries(cards)) {
    totalSeen++;
    const dueDate = card.due instanceof Date ? card.due : new Date(card.due);
    const isDue = dueDate <= now;
    if (isDue) totalDueToday++;

    if (card.stability >= MASTERED_STABILITY_DAYS) {
      totalMastered++;
    } else {
      totalInLearning++;
    }
  }

  // Compute domain mastery from theta estimates
  for (const d of domainKeys) {
    const key = 'd' + d;
    const t = theta[d]?.theta ?? theta[key]?.theta ?? 0;
    domainMastery[key] = 1 / (1 + Math.exp(-t));
  }

  // Session stats
  const sessionList = Object.values(sessions);
  const totalSessions = sessionList.length;
  const lastSession = sessionList.sort((a, b) =>
    new Date(b.endTime || b.startTime) - new Date(a.endTime || a.startTime)
  )[0];

  // Skill mastery (BKT-tracked, cert-scoped)
  const skillMastery = await getSkillMastery(userId, certId);

  // Calibration (ECE-based, user-global)
  const calibrationScore = await getCalibrationScore(userId);

  // Knowledge graph: concept mastery + skill coverage (cert-scoped)
  let conceptMasteryData = null;
  let coverageData = null;
  let weakConceptsList = null;
  if (isGraphReady(certId)) {
    try {
      conceptMasteryData = await getConceptMastery(certId, userId);
      coverageData = computeCoverage(certId, skillMastery);
      weakConceptsList = await getWeakConcepts(certId, userId);
    } catch (e) { /* graph queries failed — non-fatal */ }
  }

  // Exam Readiness Score
  const { predictedPassRate, readinessBreakdown } = computeReadinessScore({
    domainMastery,
    coverageData,
    cards,
    calibrationScore,
    domainWeights: certWeights
  });

  return {
    domainMastery,
    skillMastery,
    theta: Object.fromEntries(domainKeys.map(d => ['d' + d, theta[d]?.theta ?? theta['d' + d]?.theta ?? 0])),
    calibrationScore,
    confidenceMatrix,
    predictedPassRate: Math.round(predictedPassRate * 100) / 100,
    readinessBreakdown,
    totalSessions,
    lastSessionDate: lastSession?.endTime || lastSession?.startTime || null,
    cardsSeen: totalSeen,
    cardsMastered: totalMastered,
    cardsInLearning: totalInLearning,
    cardsDueToday: totalDueToday,
    fatigueBaseline: null,
    conceptMastery: conceptMasteryData,
    skillCoverage: coverageData,
    weakConcepts: weakConceptsList
  };
}
